import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import MainLayout from '../components/layout/MainLayout';
import { useAnalytics } from '../contexts/AnalyticsContext';
import { useSubscription } from '../contexts/SubscriptionContext';

const AnalyticsPage: React.FC = () => {
  const { analyticsData, isLoading, refreshAnalytics, getMostUsedFeatures } = useAnalytics();
  const { subscription } = useSubscription();
  const [mostUsedFeatures, setMostUsedFeatures] = useState<{ feature: string; usage: number }[]>([]);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  useEffect(() => {
    if (analyticsData) {
      loadMostUsedFeatures();
    }
  }, [analyticsData]);

  const loadMostUsedFeatures = async () => {
    try {
      const features = await getMostUsedFeatures();
      setMostUsedFeatures(features);
    } catch (error) {
      console.error('Failed to load most used features:', error);
    }
  };

  const handleRefresh = () => {
    refreshAnalytics();
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
              <p className="text-gray-600">加载统计数据中...</p>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!analyticsData) {
    return (
      <MainLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">数据统计</h1>
            <p className="text-gray-600 mb-8">暂无统计数据</p>
            <button
              onClick={handleRefresh}
              className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors"
            >
              刷新数据
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">数据统计</h1>
            <p className="text-gray-600">了解您的创作数据和功能使用情况</p>
          </div>
          <div className="flex space-x-3 mt-4 sm:mt-0">
            <button
              onClick={handleRefresh}
              className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
            >
              刷新数据
            </button>
            <Link
              to="/dashboard"
              className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
            >
              返回仪表板
            </Link>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">剧本生成</p>
                <p className="text-2xl font-bold text-gray-900">
                  {analyticsData.totalUsage.scriptsGenerated}
                </p>
                <p className="text-xs text-gray-500">总数</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <span className="text-purple-600 text-xl">🎭</span>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">角色创建</p>
                <p className="text-2xl font-bold text-gray-900">
                  {analyticsData.totalUsage.charactersCreated}
                </p>
                <p className="text-xs text-gray-500">总数</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <span className="text-blue-600 text-xl">👥</span>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">分镜设计</p>
                <p className="text-2xl font-bold text-gray-900">
                  {analyticsData.totalUsage.storyboardsCreated}
                </p>
                <p className="text-xs text-gray-500">总数</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <span className="text-green-600 text-xl">🎬</span>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">项目管理</p>
                <p className="text-2xl font-bold text-gray-900">
                  {analyticsData.totalUsage.projectsCreated}
                </p>
                <p className="text-xs text-gray-500">总数</p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <span className="text-orange-600 text-xl">📁</span>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">文件导出</p>
                <p className="text-2xl font-bold text-gray-900">
                  {analyticsData.totalUsage.exportsCount}
                </p>
                <p className="text-xs text-gray-500">总数</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <span className="text-red-600 text-xl">📤</span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Most Used Features */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">功能使用统计</h2>
            <div className="space-y-4">
              {mostUsedFeatures.map((feature, index) => (
                <div key={feature.feature} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">
                      {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : '🏅'}
                    </span>
                    <span className="font-medium">{feature.feature}</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                      <div
                        className="bg-purple-600 h-2 rounded-full"
                        style={{ width: `${(feature.usage / Math.max(...mostUsedFeatures.map(f => f.usage))) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-gray-600">{feature.usage}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Popular Genres */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">热门题材</h2>
            <div className="space-y-4">
              {analyticsData.popularGenres.map((genre, index) => (
                <div key={genre.genre} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="text-lg mr-3">
                      {index === 0 ? '💕' : index === 1 ? '😄' : index === 2 ? '🕵️' : '🎭'}
                    </span>
                    <span className="font-medium">{genre.genre}</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                      <div
                        className="bg-pink-600 h-2 rounded-full"
                        style={{ width: `${(genre.count / Math.max(...analyticsData.popularGenres.map(g => g.count))) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-gray-600">{genre.count}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Usage Trends */}
        <div className="mt-8 bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">使用趋势</h2>
          <div className="h-64 flex items-end justify-between space-x-2">
            {analyticsData.usageTrends.map((trend, index) => (
              <div key={trend.date} className="flex flex-col items-center flex-1">
                <div className="text-xs text-gray-500 mb-2">
                  {new Date(trend.date).toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' })}
                </div>
                <div
                  className="bg-gradient-to-t from-purple-600 to-purple-400 rounded-t w-full transition-all duration-300 hover:from-purple-700 hover:to-purple-500"
                  style={{ height: `${(trend.usage / Math.max(...analyticsData.usageTrends.map(t => t.usage))) * 100}%` }}
                ></div>
                <div className="text-xs text-gray-600 mt-2">{trend.usage}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Pro Feature Upgrade Prompt */}
        {!subscription?.features?.hasPrioritySupport && (
          <div className="mt-8 bg-gradient-to-r from-purple-600 to-purple-800 text-white rounded-xl p-6">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="mb-4 md:mb-0">
                <h3 className="text-xl font-semibold mb-2">解锁高级分析功能</h3>
                <p className="text-purple-100">
                  升级到专业版，获得更详细的数据分析、自定义报表和API访问权限
                </p>
              </div>
              <Link
                to="/pricing"
                className="px-6 py-3 bg-white text-purple-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors"
              >
                立即升级
              </Link>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default AnalyticsPage;